'use client'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useApp } from '@/context/AppContext'
import { useLang } from '@/context/LangContext'
import { api } from '@/lib/api'
import { formatDT, getTodayDate } from '@/lib/utils'
import { Package, AlertTriangle, MessageSquare, Store, LogOut, ChevronRight, ShoppingCart, Wallet, BarChart2, Settings, Users } from 'lucide-react'
import Link from 'next/link'
import { LangSwitch } from '@/components/LangSwitch'

export default function DashboardPage() {
  const { session, logout } = useApp()
  const { t } = useLang()
  const router = useRouter()

  const [caisse, setCaisse] = useState<any>(null)
  const [alertArticles, setAlertArticles] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!session) { router.replace('/login'); return }
    loadData()
  }, [session])

  async function loadData() {
    const shopId = session!.shop.id
    const [caisseData, articles] = await Promise.all([
      api.getCaisse(shopId, getTodayDate()),
      api.getArticles(shopId),
    ])
    setCaisse(caisseData?.session || null)
    setAlertArticles((articles || []).filter((a: any) => a.stock < a.stock_alert).slice(0, 5))
    setLoading(false)
  }

  const isPatron = session?.user.role === 'patron'
  const roleLabel: Record<string, string> = {
    patron: t('role_patron'), vendeur: t('role_vendeur'), caissier: t('role_caissier'), '': '',
  }

  const navItems = [
    { href: '/vente',    icon: ShoppingCart, key: 'nav_vente',    color: 'bg-green-500',  show: true },
    { href: '/articles', icon: Package,       key: 'nav_articles', color: 'bg-blue-500',   show: true },
    { href: '/caisse',   icon: Wallet,        key: 'nav_caisse',   color: 'bg-orange-500', show: true },
    { href: '/clients',  icon: Users,         key: 'nav_clients',  color: 'bg-teal-500',   show: true },
    { href: '/rapport',  icon: BarChart2,     key: 'nav_rapport',  color: 'bg-purple-500', show: isPatron },
    { href: '/chat',     icon: MessageSquare, key: 'nav_chat',     color: 'bg-indigo-500', show: true },
    { href: '/settings', icon: Settings,      key: 'nav_settings', color: 'bg-gray-500',   show: isPatron },
  ].filter(i => i.show)

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
    </div>
  )

  const soldeTh = caisse ? parseFloat(caisse.solde_theorique) : null

  return (
    <div className="min-h-screen pb-6">
      <div className="bg-gradient-to-br from-indigo-600 to-indigo-700 px-4 pt-12 pb-20 rounded-b-[2.5rem]">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Store className="w-5 h-5 text-indigo-200" />
            <span className="text-indigo-200 text-sm font-medium">{session?.shop.name}</span>
          </div>
          <div className="flex items-center gap-2">
            <LangSwitch />
            <button onClick={logout} className="p-2 text-indigo-200 hover:text-white">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
        <h1 className="text-2xl font-bold text-white">{t('hello')}، {session?.user.name} 👋</h1>
        <p className="text-indigo-200 text-sm">{roleLabel[session?.user.role ?? ''] ?? session?.user.role}</p>
      </div>

      <div className="px-4 -mt-12 space-y-4">
        {/* Caisse */}
        <div className="bg-white rounded-3xl p-5 shadow-lg">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-gray-800">{t('caisse_today')}</h2>
            <Link href="/caisse" className="text-indigo-600 text-sm font-medium flex items-center gap-1">
              {t('details')} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          {soldeTh !== null ? (
            <>
              <div className="flex justify-between items-center">
                <span className="text-gray-500 text-sm">{t('current_balance')}</span>
                <span className="text-2xl font-bold text-gray-900">{formatDT(soldeTh)}</span>
              </div>
              {isPatron && (
                <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-gray-100">
                  <div className="text-center">
                    <p className="text-green-600 font-bold text-sm">{formatDT(parseFloat(caisse.total_ventes || 0))}</p>
                    <p className="text-gray-400 text-xs">{t('sales')}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-red-500 font-bold text-sm">{formatDT(parseFloat(caisse.total_retraits || 0))}</p>
                    <p className="text-gray-400 text-xs">{t('withdrawals')}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-orange-500 font-bold text-sm">{formatDT(parseFloat(caisse.total_charges || 0))}</p>
                    <p className="text-gray-400 text-xs">{t('charges')}</p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-2">
              <p className="text-gray-400 text-sm">{t('caisse_closed')}</p>
              <Link href="/caisse" className="text-indigo-600 font-semibold text-sm mt-1 inline-block">{t('open_caisse')}</Link>
            </div>
          )}
        </div>

        {/* Alerts stock */}
        {alertArticles.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-3xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              <h3 className="font-bold text-amber-800">{t('stock_alert')}</h3>
            </div>
            {alertArticles.map((a: any) => (
              <div key={a.id} className="flex justify-between items-center py-1">
                <span className="text-amber-700 text-sm">{a.name}</span>
                <span className="text-amber-600 font-bold text-sm bg-amber-100 px-2 py-0.5 rounded-full">{a.stock} {a.unit}</span>
              </div>
            ))}
          </div>
        )}

        {/* Nav */}
        <div className="grid grid-cols-2 gap-3">
          {navItems.map(item => (
            <Link key={item.href} href={item.href}
              className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex items-center gap-3 active:scale-95 transition-all hover:shadow-md">
              <div className={`w-11 h-11 ${item.color} rounded-xl flex items-center justify-center`}>
                <item.icon className="w-6 h-6 text-white" />
              </div>
              <span className="font-semibold text-gray-800">{t(item.key as any)}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}
